import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreOutlined } from "us.icons";
import Common from "us.common";
import { getRouteUrl } from "us.helper";
import { $AmountLabel } from "us.common/components";
import { RemitDetailsTableProps } from "./Interfaces";
import {
  RemitSummaryTableColumn,
  REMIT_PROPOSAL_TABLE_COLUMNS,
} from "./Constants";
import { ItemMenu } from "./Components";
import {
  RemitProposalActionType,
  RemitRecord,
} from "us.collection.economy/components/Remit/Interfaces";
import {
  ITEM_MENU_DEFAULT_STATE,
  RemitProposalAction,
} from "us.collection.economy/components/Remit/Constants";
import {
  getRemitProposalRecordActions,
  getTableColumnTotal,
  isMinusRemitCreditorValue,
} from "us.collection.economy/components/Remit/Functions";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";

const { $Button, $Empty, $Popover, $Tag, $TableTree } = Common.Components;

/**
 * @description - Remit details table
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2858189310/Remit+Proposal+UI+Design+and+Implementations
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 14/06/2024
 */
const RemitDetailsTable: React.FC<RemitDetailsTableProps & PropsFromRedux> =
  ({ onCallAction, remittedDetails }) => {
    const { t } = useTranslation();
    const [itemMenu, setItemMenu] = useState<{
      visible: boolean;
      id: string;
    }>(ITEM_MENU_DEFAULT_STATE);
    const remitRecords  = remittedDetails?.data ?? [];

    // auto close invoice item menu after 5 seconds
    const setAutoClose = (id: string) => {
      setTimeout(() => {
        setItemMenu({
          visible: false,
          id,
        });
      }, 5000);
    };

    // get table columns
    const getColumns: any = () => {
      const columns: Array<any> = [];
      REMIT_PROPOSAL_TABLE_COLUMNS.map(({ key, title, align }: any) => {
        let column: any = { key, dataIndex: key, align };
        if (key === RemitSummaryTableColumn.MENU) {
          column = {
            ...column,
            title: title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : "",
            width: "50px",
            customRenderChild: (text: any, record: RemitRecord) => {
              return (
                <$Popover
                  placement="bottomLeft"
                  trigger="click"
                  open={
                    itemMenu.visible && itemMenu.id == record.id
                  }
                  onOpenChange={(visible: boolean) => {
                    setItemMenu({
                      ...itemMenu,
                      visible,
                      id: record.id,
                    });
                    setAutoClose(record.id);
                  }}
                  content={
                    <ItemMenu
                      actions={getRemitProposalRecordActions(record)}
                      onCallAction={(actionType: RemitProposalActionType) => {
                        setItemMenu(ITEM_MENU_DEFAULT_STATE);
                        onCallAction(record, actionType);
                      }}
                    />
                  }
                  destroyTooltipOnHide
                >
                  <$Button
                    id="btnItemMenu"
                    type="default"
                    icon={<MoreOutlined />}
                    size="small"
                  />
                </$Popover>
              );
            },
          };
        } else if (key === RemitSummaryTableColumn.CREDITOR_NO) {
          let index = 1; 
          column = {
            ...column,
            key: index++,
            title: title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : "",
            customFilter: true,
            showFilters: true,
            customRenderChild: (text: any, record: RemitRecord) => {
              return (
                <a
                  data-testid="link-creditor-no"
                  onClick={() => getRouteUrl.creditor(record.creditorNo)}
                >
                  {text}
                </a>
              );
            },
            customSorter: (a: any, b: any) => a - b,
          };
        } else if (key === RemitSummaryTableColumn.VAT_DEDUCTED) {
          column = {
            ...column,
            with: "100px",
            title: () => (
              <div className="mr-2">
                <div>{t(`US.COLLECTION.ECONOMY:REMIT.VAT_DEDUCTED`)}</div>
                <div
                  className="header-custom-amount"
                  data-testid="vat-deducted-total"
                >
                  <$AmountLabel
                    value={getTableColumnTotal(key, remitRecords)}
                  />
                </div>
              </div>
            ),
            customRenderChild: (
              text: any,
              { isInvoiced, vatDeducted }: RemitRecord
            ) => {
              return (
                <div data-testid="vat-deducted-amount">
                  {isInvoiced && (
                    <$Tag color={"green"}>
                      {t("US.COLLECTION.ECONOMY:REMIT.INVOICED")}
                    </$Tag>
                  )}
                  <$AmountLabel value={vatDeducted} />
                </div>
              );
            },
            customSorter: (a: any, b: any) => a - b,
          };
        } else {
          column = {
            ...column,
            with: "100px",
            title: () => (
              <div className="mr-2">
                <div>
                  {title ? t(`US.COLLECTION.ECONOMY:REMIT.${title}`) : ""}
                </div>
                <div
                  className="header-custom-amount"
                  data-testid={`${key}Total`}
                >
                  <$AmountLabel
                    value={getTableColumnTotal(key, remitRecords)}
                  />
                </div>
              </div>
            ),
            customRenderChild: (text: any, record: any) => {
              return (
                <span data-testid={`${key}Amount`}>
                  <$AmountLabel value={record[key]} />
                </span>
              );
            },
          };
        }
        columns.push(column);
      });
      return columns;
    };

    return (
      <$TableTree
        rowKey={(record: RemitRecord) => record?.id}
        data={remitRecords}
        size="small"
        className="mt-3 header-custom-tag"
        //rowClassName={getRowClass}
        onSort={(sortData, dataSource) => {
          return sortData(dataSource);
        }}
        onFilter={(searchData, dataSource) => {
          return searchData(dataSource);
        }}
        filterOnType
        resetOnSourceChange
        bordered
        showResetAllFilters
        pagination={{
          defaultPageSize: 20,
        }}
        scroll={{
          x: 900,
          y: "calc(100vh - 265px)",
        }}
        columns={getColumns()}
        onRow={(record, _rowIndex) => {
          return {
            onDoubleClick: (_event) =>
              onCallAction(record, RemitProposalAction.VIEW_REMIT_DETAILS),
          };
        }}
        locale={{
          emptyText: (
            <$Empty
              image={$Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="text-center">
                  <div>{t("US.COLLECTION.COMMON:COMMON.NO_DATA")}</div>
                  <div>
                    {t(
                      "US.COLLECTION.ECONOMY:REMITTED_DETAILS.SELECT_CREDITORS_TO_VIEW_REMIT"
                    )}
                  </div>
                </div>
              }
            />
          ),
        }}
      />
    );
  };

const mapStateToProps = (state: RootState) => {
  const { remitDetails } = state;

  const { remittedDetails } = remitDetails;
  return {
    remittedDetails,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(RemitDetailsTable);
